/**
 * Helper de DISPLAY para invocaciones: dado un spell que invoca, resuelve las
 * características BASE de la criatura (perfil + template de NPC) para mostrarlas
 * en la ventana de info del hechizo y en su tooltip. Puro (client + server).
 *
 * Muestra stats BASE (sin los bonos `summon*` del owner, que son dinámicos).
 * Ver docs/SUMMONS.md.
 */

import type { SpellDefinition, SpellEffectSummon } from '../types/spell';
import type { SummonRole } from '../types/summon';
import { getSummonProfile } from '../data/summonProfiles';
import { getNPCTemplate } from '../data/npcs';

export interface SummonDisplayInfo {
  /** Nombre en español de la criatura invocada. */
  name: string;
  /** Rol: hostil (pega) o soporte (asiste al owner). */
  role: SummonRole;
  /** Especialidad legible (para soporte: qué hace). undefined si es hostil puro. */
  specialtyEs?: string;
  /** Sprites para el preview / iconos. */
  bodyId: number;
  headId: number;
  /** Cuántas criaturas invoca el proceso (base, sin `summonPackLeader`). */
  count: number;
  /** Vida base de la criatura. */
  hp: number;
  /** Daño base (min-max). */
  minDamage: number;
  maxDamage: number;
  /** Velocidad de ataque base (multiplicador; 1.0 = normal). */
  attackSpeed: number;
  /** Velocidad de movimiento base (multiplicador). */
  moveSpeed: number;
  /** Chance de crítico base (decimal). Los summons parten de 0 salvo bonos del owner. */
  critChance: number;
  /** Reserva de maná del toggle (decimal 0..1), si aplica. */
  manaReservePercent?: number;
  /** Duración en ms (0 = permanente). */
  durationMs: number;
  /** ms de resurrección si es permanente (0 = no revive). */
  respawnMs: number;
}

/** Devuelve el effect summon de un spell, o undefined. */
export function getSpellSummonEffect(spell: SpellDefinition): SpellEffectSummon | undefined {
  return spell.effects.find((e): e is SpellEffectSummon => e.type === 'summon');
}

/**
 * Resuelve la info de display de la invocación de un spell, o null si el spell
 * no invoca / falta el perfil o el template.
 */
export function getSummonDisplayForSpell(spell: SpellDefinition): SummonDisplayInfo | null {
  const effect = getSpellSummonEffect(spell);
  if (!effect) return null;
  const profile = getSummonProfile(effect.profileId ?? effect.npcTemplateId);
  if (!profile) return null;
  const template = getNPCTemplate(profile.npcTemplateId);
  if (!template) return null;

  const specialtyEs =
    profile.role === 'support'
      ? summonSupportSpecialtyEs(profile.supportSpellIds)
      : undefined;

  return {
    name: profile.nameEs,
    role: profile.role,
    specialtyEs,
    bodyId: template.bodyId ?? 1,
    headId: template.headId ?? 0,
    count: profile.count,
    hp: template.maxHp ?? 0,
    minDamage: template.minDamage ?? 1,
    maxDamage: template.maxDamage ?? 5,
    attackSpeed: template.attackSpeed ?? 1,
    moveSpeed: template.moveSpeed ?? 1,
    critChance: 0,
    manaReservePercent: profile.manaReservePercent,
    durationMs: profile.durationMs,
    respawnMs: profile.respawnMs,
  };
}

/** Etiqueta legible de la especialidad de un summon de soporte. */
function summonSupportSpecialtyEs(supportSpellIds?: string[]): string {
  if (!supportSpellIds || supportSpellIds.length === 0) return 'Soporte';
  return 'Soporte';
}
